import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { find } from 'lodash';
import { Table } from 'components/Base/Table';
import {
  TEventAttendanceListUI,
  TEventRegistrantUI,
  TAttendancesGetUI,
  ERegistrantsColumnKey,
  EAttendanceColumnKey,
  TAttendanceEditUI,
} from 'data/types/registrants.types';
import {
  eventsApi,
  useExportEventAttendanceMutation,
} from 'redux/apiSlice/events.slice';
import { useParams } from 'react-router-dom';
import {
  useGetEventRegistrantsQuery,
  useUpdateEventRegistrantNotificationMutation,
} from 'redux/apiSlice/registrants.slice';
import {
  ENotificationType,
  useShowToastNotification,
} from 'components/Base/Notification';
import useTable from 'components/Base/Table/hooks/useTable';
import {
  ETableStoragePaths,
  TColumnsState,
} from 'components/Base/Table/Table.types';
import { TabBodyWrapper } from 'components/Base/Tabs';
import ExportDropdown from 'components/Shared/ExportDropdown';
import AttendanceBanner from 'components/Shared/AttendanceBanner';
import { appLocalStorage, EStorageNames } from 'helpers/storage';
import DeleteRegistrantModal from 'components/Shared/DeleteRegistrantModal/DeleteRegistrantModal';
import { useDeleteEventRegistrant } from 'hooks/useDeleteEventRegistrant';
import { useAppSelector } from 'redux/store';
import { EUserType } from 'data/types/user.types';
import Button from 'components/Base/Button/Button';
import { PersonRemoveOutline } from 'assets/icon/icon';
import { TAttendanceListProps } from './AttendanceList.types';
import useGetData from './AttendanceList.data';
import useAgeGroupLanguageFilter from '../../RegistrantsTab/RegistrantsList/useAgeGroupLanguageFilter';

const ADMIN_TYPES = [
  EUserType.READY_ADMIN,
  EUserType.SUPER_ADMIN,
  EUserType.ELC_LEAD,
  EUserType.CAFE_COORDINATOR,
  EUserType.ELC_PM,
];

const COLUMN_KEY_TO_EXPORT_NAME: Record<string, string> = {
  [EAttendanceColumnKey.FIRST_NAME]: 'firstName',
  [EAttendanceColumnKey.LAST_NAME]: 'lastName',
  [EAttendanceColumnKey.EMAIL]: 'email',
  [EAttendanceColumnKey.PHONE]: 'phone',
  [EAttendanceColumnKey.PHOTO]: 'photo',
  customQuestionAnswer: 'questionAnswer',
  [EAttendanceColumnKey.ATTENDANCE]: 'attendance',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EAttendanceColumnKey.FIRST_NAME]: 'FIRST_NAME',
  [EAttendanceColumnKey.LAST_NAME]: 'LAST_NAME',
};

const AttendanceList: FC<TAttendanceListProps> = ({
  eventId,
  eventStatus,
  eventType,
  title,
  partType = '',
  sessionDateTime,
  partDateTime,
  sessions,
  customQuestion,
}) => {
  const [attendanceData, setAttendanceData] = useState<
    TEventAttendanceListUI[]
  >([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedRegistrant, setSelectedRegistrant] =
    useState<TEventAttendanceListUI | null>(null);
  const { deleteEventRegistrant, isLoading: isDeleteLoading } =
    useDeleteEventRegistrant();
  const { showToastNotification } = useShowToastNotification();
  const [updateNotification] = useUpdateEventRegistrantNotificationMutation();

  const usersData = useAppSelector(state => state.userSlice);
  const isAdmin = useMemo(() => {
    const user = usersData?.user;
    return user?.type?.some((type: EUserType) => ADMIN_TYPES.includes(type));
  }, [usersData?.user]);

  const handleDeleteClick = (record: TEventAttendanceListUI) => {
    if (record.isEventClosed) return;
    setSelectedRegistrant(record);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedRegistrant(null);
  };

  const handleDeleteConfirm = async () => {
    if (selectedRegistrant && eventId) {
      const success = await deleteEventRegistrant({
        eventId,
        registrantId: selectedRegistrant.registrantId,
      });

      if (success) {
        setIsDeleteModalOpen(false);
        setSelectedRegistrant(null);
      }
    }
  };

  const handleNotificationToggle = async (
    record: { registrantId: string },
    checked: boolean,
  ) => {
    setAttendanceData(prev =>
      prev.map(item =>
        item.registrantId === record.registrantId
          ? { ...item, notificationsEnabled: checked }
          : item,
      ),
    );
    try {
      await updateNotification({
        eventId,
        registrantId: record.registrantId,
        notificationDisabled: !checked,
      }).unwrap();
      showToastNotification({
        message: `Notifications ${
          checked ? 'enabled' : 'disabled'
        } successfully`,
        type: ENotificationType.SUCCESS,
      });
    } catch {
      setAttendanceData(prev =>
        prev.map(item =>
          item.registrantId === record.registrantId
            ? { ...item, notificationsEnabled: !checked }
            : item,
        ),
      );
      showToastNotification({
        message: 'Failed to update notification preference',
        type: ENotificationType.ERROR,
      });
    }
  };

  const { defaultColumns, defaultSortParam } = useGetData({
    eventId,
    eventStatus,
    eventType,
    sessionDateTime,
    onNotificationToggle: handleNotificationToggle,
  });

  const filteredDefaultColumns = useMemo(
    () =>
      customQuestion
        ? defaultColumns
        : defaultColumns.filter(
            col => col.dataIndex !== 'customQuestionAnswer',
          ),
    [defaultColumns, customQuestion],
  );

  const storageKey = `${ETableStoragePaths.ATTENDANCE_LIST}-${eventId}`;

  const {
    page,
    pageSize,
    sortColumn,
    setPageSize,
    handleTableChange,
    selectedFilters,
    hasFilters,
    columns,
    setColumns,
    onFiltersReset,
    selectedRows,
    setSelectedRows,
  } = useTable<TEventAttendanceListUI>({
    storagePath: storageKey,
    defaultColumns: filteredDefaultColumns,
    defaultSortColumn: defaultSortParam,
  });

  const params = useParams();
  const { id } = params as { id: string };

  const { data: eventDetailsCachedData } =
    eventsApi.endpoints.getEventById.useQueryState(
      {
        id,
      },
      { skip: !id },
    );

  const eventClasses = eventDetailsCachedData?.classes.value || [];

  const ageGroupLanguageFilters = useAgeGroupLanguageFilter(eventClasses);
  useEffect(() => {
    if (ageGroupLanguageFilters) {
      setColumns(
        columns.map(item => {
          if (
            item.key ===
              ERegistrantsColumnKey.FILTER_BY_LANGUAGE_AND_AGE_GROUP &&
            ageGroupLanguageFilters
          ) {
            item.filters = ageGroupLanguageFilters;
          }
          return item;
        }),
      );
    }
  }, [eventClasses]);

  const { data: registrantsData, isFetching } = useGetEventRegistrantsQuery(
    {
      page: 0,
      pageSize: 2000,
      sortColumn,
      filters: selectedFilters,
      eventId,
    },
    {
      skip: !eventId,
    },
  );

  const processAttendanceData = (content: TEventRegistrantUI[]) => {
    if (!content?.length) return [];

    return content
      .filter(item => item.status !== 'UNREGISTERED')
      .map((item: TEventRegistrantUI) => {
        const {
          firstName,
          lastName,
          childrenNames,
          spanishAgeGroups,
          englishAgeGroups,
          attendances,
          allowedTakePicture,
          countOfChildren,
          orientationKit,
          registrantId,
          status,
          materialsLanguage,
          email,
          phone,
          customQuestionAnswer,
        } = item;

        const currentAttendance = find(attendances, {
          partType,
        }) as TAttendancesGetUI;

        const attendance = currentAttendance?.attendanceStatus;
        const currentAttendanceSessionId = currentAttendance?.partAttendanceId;

        return {
          firstName,
          lastName,
          photo: allowedTakePicture,
          childrenNames,
          spanishAgeGroups,
          englishAgeGroups,
          attendance,
          countOfChildren,
          kits: currentAttendance?.kitCount || 0,
          binders: {
            received: orientationKit ? orientationKit.received : false,
            requested: orientationKit ? orientationKit.requested : false,
          },
          attendances: attendances as TAttendanceEditUI[],
          registrantId,
          currentAttendanceSessionId,
          sessionDateTime,
          partDateTime,
          sessions: sessions as any,
          status,
          materialsLanguage,
          email,
          phone,
          customQuestionAnswer,
          notificationsEnabled: item.notificationsEnabled ?? true,
          minimalRegistration: !!item.minimalRegistration,
          isEventClosed: item.isEventClosed,
          hasAttendedEvent: item.hasAttendedEvent,
          nonDeleteReason: item.nonDeleteReason,
        };
      });
  };

  useEffect(() => {
    if (registrantsData) {
      const data = processAttendanceData(registrantsData.content);
      setAttendanceData(data);
    }
  }, [registrantsData]);

  const canRemove = registrantsData?.allowedOperations?.canRemove;

  useEffect(() => {
    if (!canRemove || !isAdmin) return;
    setColumns(prev =>
      prev.map(col => {
        if (col.key !== 'actions') return col;
        return {
          ...col,
          render: (_: unknown, record: TEventAttendanceListUI) => {
            const isDeletable =
              !record.hasAttendedEvent &&
              !record.isEventClosed &&
              !record.nonDeleteReason;
            if (!isDeletable) {
              return (
                <Button
                  variant="ghost"
                  tooltip={
                    record.nonDeleteReason ||
                    (record.hasAttendedEvent
                      ? 'This registrant cannot be removed because they have attended the event.'
                      : "Can't delete registrant")
                  }
                  icon={<PersonRemoveOutline size={24} fill="#808080" />}
                  disabled
                  className="cursor-not-allowed"
                />
              );
            }
            return (
              <Button
                variant="ghost"
                tooltip="Delete Registrant"
                icon={<PersonRemoveOutline size={24} fill="#656565" />}
                onClick={() => handleDeleteClick(record)}
              />
            );
          },
        };
      }),
    );
  }, [canRemove, isAdmin]);

  const [exportAttendance, { isLoading: isExporting }] =
    useExportEventAttendanceMutation();

  const getVisibleExportColumns = useCallback(() => {
    const tableSettings = appLocalStorage.getItem(EStorageNames.tableSettings);
    const columnsState = tableSettings?.[storageKey] as TColumnsState[];
    const exportColumns = columnsState
      ? columnsState
          .filter(col => col.show)
          .map(col => COLUMN_KEY_TO_EXPORT_NAME[col.key as string])
          .filter(Boolean)
      : Object.values(COLUMN_KEY_TO_EXPORT_NAME);
    return customQuestion
      ? exportColumns
      : exportColumns.filter(col => col !== 'questionAnswer');
  }, [storageKey, customQuestion]);

  const buildExportFilters = useCallback(
    (ids?: string[]) => {
      const filters: Record<string, unknown> = {
        eventId,
      };
      if (ids?.length) {
        filters.ids = ids;
      }
      if (partType) {
        filters.partType = partType;
      }
      if (selectedFilters?.firstName?.[0]) {
        filters.firstName = selectedFilters.firstName[0] as string;
      }
      if (selectedFilters?.lastName?.[0]) {
        filters.lastName = selectedFilters.lastName[0] as string;
      }
      if (selectedFilters?.email?.[0]) {
        filters.email = selectedFilters.email[0] as string;
      }
      return filters;
    },
    [eventId, partType, selectedFilters],
  );

  const buildExportSort = useCallback(() => {
    if (!sortColumn.sortField || !sortColumn.sortDirection) return undefined;
    const field = SORT_FIELD_MAP[sortColumn.sortField];
    if (!field) return undefined;
    return { field, direction: sortColumn.sortDirection };
  }, [sortColumn]);

  const handleExportAll = useCallback(async () => {
    try {
      const exportColumns = getVisibleExportColumns();
      // When firstName/lastName filters are active, use row IDs from the
      // already-filtered table data since the BE export endpoint does not
      // support these text filters yet.
      const hasNameFilters =
        selectedFilters?.firstName?.[0] || selectedFilters?.lastName?.[0];
      const ids =
        hasNameFilters && attendanceData.length > 0
          ? attendanceData.map(row => row.registrantId)
          : undefined;
      await exportAttendance({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportAttendance,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
    selectedFilters,
    attendanceData,
  ]);

  const handleExportSelected = useCallback(async () => {
    const { selectedRowsKeys } = selectedRows;

    if (!selectedRowsKeys?.length) {
      showToastNotification({
        message: 'Please select at least one row to export',
        type: ENotificationType.ERROR,
      });
      return;
    }

    try {
      const ids = selectedRowsKeys.map(key => String(key));
      const exportColumns = getVisibleExportColumns();
      await exportAttendance({
        columns: exportColumns,
        filters: buildExportFilters(ids),
        ...(buildExportSort() && { sort: buildExportSort() }),
      }).unwrap();
    } catch {
      showToastNotification({
        message: 'Export failed. Please try again.',
        type: ENotificationType.ERROR,
      });
    }
  }, [
    exportAttendance,
    selectedRows,
    getVisibleExportColumns,
    buildExportFilters,
    buildExportSort,
    showToastNotification,
  ]);

  const exportAction = useMemo(
    () => (
      <ExportDropdown
        key="export-actions"
        onExportAll={handleExportAll}
        onExportSelected={handleExportSelected}
        isExporting={isExporting}
      />
    ),
    [handleExportAll, handleExportSelected, isExporting],
  );

  // GROW-4332: read the current session's window state from any row's matching
  // attendance — BE sends the same state for every registrant of a given part.
  const currentSessionWindow = useMemo(() => {
    const firstAttendance = registrantsData?.content
      ?.flatMap(r => r.attendances || [])
      .find(a => a.partType === partType);
    return {
      attendanceState: firstAttendance?.attendanceState,
      attendanceOpensAt: firstAttendance?.attendanceOpensAt,
      attendanceClosesAt: firstAttendance?.attendanceClosesAt,
    };
  }, [registrantsData, partType]);

  return (
    <>
      <AttendanceBanner
        attendanceState={currentSessionWindow.attendanceState}
        attendanceOpensAt={currentSessionWindow.attendanceOpensAt}
        attendanceClosesAt={currentSessionWindow.attendanceClosesAt}
        textKeyPrefix="descriptions.admin-portal.events.details.attendance-list"
      />
      <TabBodyWrapper>
        <Table<TEventAttendanceListUI>
          tableTitle={title}
          tabHeight={44}
          onFiltersReset={onFiltersReset}
          hasFilters={hasFilters}
          currentPage={page}
          columns={columns}
          additionalActions={[exportAction]}
          dataSource={attendanceData}
          loading={isFetching}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalDataCount={registrantsData?.totalElements}
          onChange={handleTableChange}
          rowKey={record => record.registrantId}
          storagePath={storageKey}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
        />
      </TabBodyWrapper>
      <DeleteRegistrantModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleteLoading}
      />
    </>
  );
};

export default AttendanceList;
